import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { CalendarDays, Sparkles } from 'lucide-react';
import CourseCalendar from '../../courses-calender/CourseCalendar';
import StudentsLifeSection from '../../students-life-section/StudentsLifeSection';
import { getFormationALaCarteTrainings } from '../../../../components/blog-section/formationALaCarteTrainings';
import CalendrierHero from './ClendrierHero';

const CALENDAR_STORAGE_KEY = 'clevory_course_calendar_payload';

const MAIN_COURSE_EVENTS = [
	{
		id: 'agentic-ai-main',
		title: 'Formation Agentic AI',
		name: 'Formation Agentic AI',
		start: '2026-06-15',
		end: '2026-06-16',
		allDay: true,
		type: 'course',
		location: 'Clevory Academy',
		description:
			'Formation Intelligence Artificielle et conception d’agents IA.',
		image:
			'/assets/images/events-landing-page/formation intellignece artificielle.png',
		link: '/formations/agentic-ia',
	},
	{
		id: 'it-sales-main',
		title: 'Formation IT SALES',
		name: 'Formation IT SALES',
		start: '2026-09-15',
		end: '2026-09-16',
		allDay: true,
		type: 'course',
		location: 'Clevory Academy',
		description:
			'Formation IT Sales pour apprendre à vendre des solutions technologiques.',
		image: '/assets/images/events-landing-page/IT SALES.png',
		link: '/formations/it-sales',
	},
];

function createCalendarPayload(events, source = 'calendrier-page') {
	return {
		source,
		title: 'Calendrier des formations',
		events: Array.isArray(events) ? events : [],
		timestamp: Date.now(),
	};
}

function saveCalendarPayload(payload) {
	try {
		sessionStorage.setItem(CALENDAR_STORAGE_KEY, JSON.stringify(payload));
	} catch (error) {
		console.error(
			'Impossible de sauvegarder le calendrier en sessionStorage:',
			error,
		);
	}
}

const CalendrierPage = () => {
	const [searchParams] = useSearchParams();
	const location = useLocation();

	const [formationALaCarteTrainings, setFormationALaCarteTrainings] = useState(
		[],
	);
	const [showStudentsLife, setShowStudentsLife] = useState(false);
	const [showCalendar, setShowCalendar] = useState(true);
	const [selectedTrainingId, setSelectedTrainingId] = useState(
		Number(searchParams.get('training')) || null,
	);

	const [calendarPayload, setCalendarPayload] = useState(() => {
		const stateEvents = location.state?.calendarEvents;

		if (Array.isArray(stateEvents) && stateEvents.length > 0) {
			return createCalendarPayload(stateEvents, 'calendrier-page-initial');
		}

		try {
			const stored = sessionStorage.getItem(CALENDAR_STORAGE_KEY);
			if (stored) {
				const parsed = JSON.parse(stored);

				if (Array.isArray(parsed?.events) && parsed.events.length > 0) {
					return parsed;
				}
			}
		} catch (error) {
			console.error('Erreur lecture sessionStorage calendrier:', error);
		}

		return null;
	});

	const studentsLifeRef = useRef(null);
	const calendarRef = useRef(null);

	const allCalendarEvents = useMemo(() => {
		return [...MAIN_COURSE_EVENTS, ...formationALaCarteTrainings];
	}, [formationALaCarteTrainings]);

	const selectedTraining = useMemo(() => {
		if (!formationALaCarteTrainings.length) return null;

		return (
			formationALaCarteTrainings.find(
				(item) => item.id === Number(selectedTrainingId),
			) || formationALaCarteTrainings[0]
		);
	}, [selectedTrainingId, formationALaCarteTrainings]);

	const fallbackCalendarPayload = useMemo(() => {
		if (!allCalendarEvents.length) return null;

		return createCalendarPayload(allCalendarEvents, 'calendrier-page-fallback');
	}, [allCalendarEvents]);

	const effectiveCalendarPayload = calendarPayload || fallbackCalendarPayload;

	const scrollToStudentsLife = () => {
		setTimeout(() => {
			studentsLifeRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		}, 120);
	};

	const scrollToCalendar = () => {
		setTimeout(() => {
			calendarRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		}, 120);
	};

	const handleShowAllPublishedEvents = () => {
		const nextPayload = createCalendarPayload(
			allCalendarEvents,
			'calendrier-page-show-all-published',
		);

		setCalendarPayload(nextPayload);
		saveCalendarPayload(nextPayload);
		setShowCalendar(true);
		scrollToCalendar();
	};

	useEffect(() => {
		let isMounted = true;

		const loadTrainings = async () => {
			try {
				const data = await getFormationALaCarteTrainings();

				if (!isMounted) return;

				const safeFormationALaCarteTrainings = Array.isArray(data) ? data : [];
				setFormationALaCarteTrainings(safeFormationALaCarteTrainings);

				const shouldOpenCalendar =
					location.state?.openCalendar ||
					searchParams.get('open') === 'calendar' ||
					!location.state?.openStudentsLife;

				const stateEvents = location.state?.calendarEvents;

				if (Array.isArray(stateEvents) && stateEvents.length > 0) {
					const nextPayload = createCalendarPayload(
						stateEvents,
						'calendrier-page-state',
					);

					setCalendarPayload(nextPayload);
					saveCalendarPayload(nextPayload);
				} else if (shouldOpenCalendar) {
					const nextPayload = createCalendarPayload(
						[...MAIN_COURSE_EVENTS, ...safeFormationALaCarteTrainings],
						'calendrier-page-all-courses',
					);

					setCalendarPayload(nextPayload);
					saveCalendarPayload(nextPayload);
				}
			} catch (error) {
				console.error(
					'Erreur lors du chargement des formations à la carte:',
					error,
				);

				if (!isMounted) return;

				const nextPayload = createCalendarPayload(
					MAIN_COURSE_EVENTS,
					'calendrier-page-main-courses-fallback',
				);

				setCalendarPayload(nextPayload);
				saveCalendarPayload(nextPayload);
			}
		};

		loadTrainings();

		return () => {
			isMounted = false;
		};
	}, [location.state, searchParams]);

	useEffect(() => {
		const trainingFromState = location.state?.selectedTrainingId;
		const trainingFromQuery = Number(searchParams.get('training')) || null;

		const shouldOpenStudentsLife =
			location.state?.openStudentsLife ||
			searchParams.get('open') === 'students-life' ||
			!!trainingFromQuery;

		const shouldOpenCalendar =
			location.state?.openCalendar ||
			searchParams.get('open') === 'calendar' ||
			!shouldOpenStudentsLife;

		const stateEvents = location.state?.calendarEvents;

		if (trainingFromState || trainingFromQuery) {
			setSelectedTrainingId(trainingFromState || trainingFromQuery);
		}

		if (Array.isArray(stateEvents) && stateEvents.length > 0) {
			const nextPayload = createCalendarPayload(
				stateEvents,
				'calendrier-page-state-update',
			);

			setCalendarPayload(nextPayload);
			saveCalendarPayload(nextPayload);
		}

		if (shouldOpenStudentsLife) {
			setShowStudentsLife(true);
			scrollToStudentsLife();
		}

		if (shouldOpenCalendar) {
			setShowCalendar(true);
			scrollToCalendar();
		}
	}, [location.state, searchParams]);

	useEffect(() => {
		const handleOpenTraining = (event) => {
			const trainingId = event.detail?.trainingId;

			if (trainingId) {
				setSelectedTrainingId(trainingId);
			}

			setShowStudentsLife(true);
			scrollToStudentsLife();
		};

		const handleOpenCalendar = (event) => {
			const payload = event.detail || null;
			const incomingEvents = payload?.events;

			if (Array.isArray(incomingEvents) && incomingEvents.length > 0) {
				const nextPayload = {
					...createCalendarPayload(
						incomingEvents,
						payload?.source || 'calendrier-page-event',
					),
					current: payload?.current || null,
				};

				setCalendarPayload(nextPayload);
				saveCalendarPayload(nextPayload);
			} else {
				handleShowAllPublishedEvents();
			}

			setShowCalendar(true);
			scrollToCalendar();
		};

		window.addEventListener(
			'formationALaCarte:openTraining',
			handleOpenTraining,
		);

		window.addEventListener(
			'formationALaCarte:openCalendar',
			handleOpenCalendar,
		);

		return () => {
			window.removeEventListener(
				'formationALaCarte:openTraining',
				handleOpenTraining,
			);
			window.removeEventListener(
				'formationALaCarte:openCalendar',
				handleOpenCalendar,
			);
		};
	}, [allCalendarEvents]);

	const handleShowCalendar = (payload = null) => {
		if (payload?.events?.length) {
			const nextPayload = {
				...createCalendarPayload(
					payload.events,
					payload?.source || 'students-life',
				),
				current: payload?.current || null,
			};

			setCalendarPayload(nextPayload);
			saveCalendarPayload(nextPayload);
		} else {
			handleShowAllPublishedEvents();
			return;
		}

		setShowCalendar(true);
		scrollToCalendar();
	};

	return (
		<>
			<CalendrierHero />

			{showStudentsLife && selectedTraining && (
				<div ref={studentsLifeRef} id='students-life'>
					<StudentsLifeSection
						training={selectedTraining}
						allTrainings={formationALaCarteTrainings}
						onViewCalendar={handleShowCalendar}
					/>
				</div>
			)}

			<div ref={calendarRef} id='course-calendar-section'>
				<div className='calendrier-calendar-actions'>
					<div className='calendrier-calendar-actions__content'>
						<span className='calendrier-calendar-actions__eyebrow'>
							<CalendarDays size={17} />
							Calendrier interactif
						</span>

						<h2 className='calendrier-calendar-actions__title'>
							Consultez toutes les formations publiées
						</h2>
					</div>

					<button
						type='button'
						className='calendrier-show-all-btn'
						onClick={handleShowAllPublishedEvents}>
						<span className='calendrier-show-all-btn__shine' />
						<span className='calendrier-show-all-btn__icon'>
							<Sparkles size={18} />
						</span>
						<span className='calendrier-show-all-btn__text'>
							Afficher tous les événements publiés
						</span>
					</button>
				</div>

				{showCalendar && effectiveCalendarPayload && (
					<CourseCalendar calendarData={effectiveCalendarPayload} />
				)}
			</div>
		</>
	);
};

export default CalendrierPage;
